import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  CAD_TO_USD,
  formatPrice,
  getDisplayPriceCad,
  plansProposes,
  type BillingPeriod,
  type PlanDescriptor,
} from '@/components/manager/subscription/plans'
import type { SubscriptionPlan } from '@/api/subscription'
import type { Currency } from '@/hooks/usePricingParams'

interface Props {
  billing: BillingPeriod
  currency: Currency
  locale: string
  onCheckout: (planSlug: SubscriptionPlan, billing: BillingPeriod, isTrial: boolean) => void
  loadingPlanSlug?: SubscriptionPlan | null
}

// Les libellés vivent désormais dans les fichiers de langue
// (pricing.features.*, pricing.taglines.*). Ce qui suit ne sert plus que de
// repli si une clé venait à manquer — le suffixe _FR est conservé pour
// signaler qu'il s'agit bien de français, jamais affiché en anglais.
const FEATURES_FR: Record<string, string[]> = {
  solo: [
    'Numéro local canadien par utilisateur',
    'Appels et textos illimités CA/US',
    "Agents IA et flux d'appels",
    'Rendez-vous : Google, Microsoft',
    'Transcriptions et résumés IA',
    'API, Zapier, ChatGPT et Claude',
    'Apps mobile et bureau',
  ],
  team: [
    'Numéros partagés et groupes',
    'Horaires par utilisateur',
    "CRM d'équipe : HubSpot, Pipedrive, Zoho, Slack",
    'Support par courriel et clavardage',
  ],
  business: [
    'Enregistrement automatique des appels',
    "Power dialer et campagnes d'appels",
    "Analytics d'équipe",
    'Écoute et chuchotement en direct',
    'Support prioritaire',
  ],
}

const PLAN_NAMES: Record<string, { name: string; tagline: string; badge?: string }> = {
  solo: { name: 'Solo', tagline: 'Pour les indépendants et petits commerces' },
  team: {
    name: 'Team',
    tagline: 'Pour une équipe',
    badge: 'LE PLUS POPULAIRE',
  },
  business: { name: 'Business', tagline: "Pour piloter les appels de l'équipe" },
}

export function PricingPlanGrid({
  billing,
  currency,
  locale,
  onCheckout,
  loadingPlanSlug,
}: Props) {
  const { t } = useTranslation()
  // Une seule vue : les trois plans, l'IA (réceptionniste) est à part.
  const plans = plansProposes()
  const isSingle = plans.length === 1
  const isPair = plans.length === 2

  return (
    <section className="relative pt-6 sm:pt-8 pb-20">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8 relative z-10">
        <div
          className={cn(
            'grid gap-4 sm:gap-5 animate-in fade-in duration-300',
            isSingle && 'grid-cols-1 max-w-md mx-auto',
            isPair && 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto',
            !isSingle && !isPair && 'grid-cols-1 md:grid-cols-3 max-w-6xl mx-auto',
          )}
        >
          {plans.map((plan) => (
            <PlanCardLarge
              key={plan.key}
              plan={plan}
              billing={billing}
              currency={currency}
              locale={locale}
              onCheckout={onCheckout}
              isLoading={loadingPlanSlug === plan.key}
              full={isSingle}
            />
          ))}
        </div>

        <p className="mt-8 text-center text-[13px] text-white/45">
          {t('pricing.allPlansTrial', "Tous les plans incluent 14 jours d'essai gratuit")}
        </p>
      </div>
    </section>
  )
}

interface PlanCardLargeProps {
  plan: PlanDescriptor
  billing: BillingPeriod
  currency: Currency
  locale: string
  onCheckout: (planSlug: SubscriptionPlan, billing: BillingPeriod, isTrial: boolean) => void
  isLoading: boolean
  full: boolean
}

function PlanCardLarge({
  plan,
  billing,
  currency,
  locale,
  onCheckout,
  isLoading,
  full,
}: PlanCardLargeProps) {
  const { t } = useTranslation()
  const fallbackMeta = PLAN_NAMES[plan.key] || { name: plan.key, tagline: '' }
  // returnObjects : la liste de fonctionnalités est un tableau dans le
  // fichier de langue, pas une chaîne.
  const features = t(`pricing.features.${plan.key}`, {
    returnObjects: true,
    defaultValue: FEATURES_FR[plan.key] || [],
  }) as string[]
  const meta = {
    // Le nom du plan (Solo, Team…) ne se traduit pas : c'est une marque.
    name: fallbackMeta.name,
    tagline: t(`pricing.taglines.${plan.key}`, fallbackMeta.tagline),
    badge: fallbackMeta.badge
      ? t(
          fallbackMeta.badge === 'LE PLUS POPULAIRE'
            ? 'pricing.badges.mostPopular'
            : 'pricing.badges.highAiVolume',
          fallbackMeta.badge,
        )
      : undefined,
  }
  const isAnnual = billing === 'yearly'
  const isUSD = currency === 'usd'

  const displayCad = getDisplayPriceCad(plan, billing)
  const inheritedKey = plan.inheritsFrom
  const inheritedName = inheritedKey ? PLAN_NAMES[inheritedKey]?.name : null

  const cardClasses = plan.featured
    ? 'relative rounded-2xl bg-blue-500/[0.06] ring-2 ring-blue-400 p-5 sm:p-6 transition-all flex flex-col h-full shadow-2xl shadow-blue-500/10'
    : 'relative rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 sm:p-6 transition-all flex flex-col h-full hover:bg-white/[0.06] hover:ring-white/20'

  const ctaClasses = plan.featured
    ? 'bg-blue-500 text-white hover:bg-blue-400'
    : 'bg-white text-[#0A192F] hover:bg-white/90'

  return (
    <div className={cn(cardClasses, full && 'max-w-3xl mx-auto w-full')}>
      {/* Slot badge — hauteur réservée pour aligner les CTAs entre cartes */}
      <div className="mb-3 h-6 flex items-center justify-center">
        {plan.featured && meta.badge && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/20 text-blue-200 text-[11px] font-semibold tracking-wide px-2.5 py-1">
            <span className="text-amber-300">★</span>
            {meta.badge}
          </span>
        )}
      </div>

      <div className="text-center">
        <h3 className="text-xl font-bold tracking-tight">{meta.name}</h3>
        <p className="mt-1 text-[13.5px] text-white/55 leading-snug">{meta.tagline}</p>
      </div>

      <div className="mt-5 text-center">
        {displayCad == null ? (
          <>
            <div className="text-2xl font-bold leading-tight">
              {t('pricing.customPricing', 'Tarification personnalisée')}
            </div>
            <p className="mt-2 text-[13px] text-white/55 leading-relaxed">
              {t('pricing.customPricingNote', 'Adaptée à votre volume et à vos besoins.')}
            </p>
          </>
        ) : (
          <>
            <div className="flex items-baseline justify-center gap-1">
              <span
                className={cn(
                  'text-4xl font-bold leading-none tabular-nums',
                  plan.featured ? 'text-blue-300' : 'text-white',
                )}
              >
                {formatPrice(displayCad, currency)}
              </span>
              <span className="text-base text-white/50 font-light">
                {isUSD ? '$ USD' : '$'}
              </span>
            </div>
            <p className="mt-2 text-[12.5px] text-white/55 leading-relaxed">
              {plan.key === 'solo'
                ? isAnnual
                  ? t('pricing.perMonthBilledYearly', 'Par mois, facturé annuellement.')
                  : t('pricing.perMonthBilledMonthly', 'Par mois, facturé mensuellement.')
                : isAnnual
                  ? t('pricing.perUserBilledYearly', 'Par utilisateur, facturé annuellement.')
                  : t('pricing.perUserPerMonth', 'Par utilisateur, par mois.')}
              {isAnnual && plan.yearlySavings > 0 && !isUSD && (
                <span className="block text-white/75">
                  Économie {plan.yearlySavings}${plan.key === 'solo' ? '/an' : '/an par user'}
                </span>
              )}
              {!isAnnual && plan.yearlyCad && (
                <span className="block text-white/40">
                  {t('pricing.yearlyRate', { amount: plan.yearlyCad })}
                </span>
              )}
            </p>
            {isUSD && displayCad && (
              <p className="mt-0.5 text-[11px] text-white/40">
                {t('pricing.approxCad', { amount: displayCad })}
              </p>
            )}
          </>
        )}
      </div>

      {plan.isEnterprise ? (
        <Link
          to={`/${locale}/contact?topic=enterprise`}
          className={cn(
            'mt-5 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[14px] font-semibold transition',
            ctaClasses,
          )}
        >
          {t('pricing.contactTeam', 'Contacter notre équipe')}
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      ) : (
        <div className="mt-5 flex flex-col items-stretch gap-1.5">
          <button
            type="button"
            disabled={isLoading}
            onClick={() => onCheckout(plan.key, billing, true)}
            className={cn(
              'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[14px] font-semibold transition',
              ctaClasses,
              isLoading && 'opacity-60 cursor-wait',
            )}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                {t('pricing.startTrial', 'Essayer 14 jours gratuit')}
                <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </div>
      )}

      <div className="my-4 h-px bg-white/10" />

      {inheritedName && (
        <p className="text-[12px] text-white/55 mb-2">
          {t('pricing.everythingInPrefix', 'Tout dans')}{' '}
          <span className="text-white/80">{inheritedName}</span>
          {t('pricing.everythingInSuffix', ', plus :')}
        </p>
      )}
      <ul className="space-y-1.5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] text-white/80">
            <span
              className={cn(
                'mt-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full shrink-0',
                plan.featured ? 'bg-blue-500/20 text-blue-300' : 'bg-white/10 text-white/70',
              )}
            >
              <Check className="w-2.5 h-2.5" />
            </span>
            <span className="leading-snug">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Re-export pour usage externe
export { CAD_TO_USD }
